import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, Users, UsersRound, Edit, Trash2, Eye, Filter, Copy, Sparkles } from 'lucide-react';
import { specialEventsService } from '../../services';
import { useNotification } from '../../contexts/NotificationContext';
import { useConfirmation } from '../../hooks/useModals';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { LoadingPage } from '../../components/ui/Loading';
import { formatDate } from '../../utils/dateUtils';

const SpecialEventsList = () => {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { modalState: confirmState, confirm, close: closeConfirm } = useConfirmation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    type: ''
  });

  useEffect(() => {
    loadEvents();
  }, [filters]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Carregando eventos especiais...');
      const data = await specialEventsService.getAll(filters);
      console.log('✅ Dados recebidos:', data);
      setEvents(data.events || []);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao carregar eventos';
      setError(errorMessage);
      console.error('❌ Erro ao carregar eventos:', err);
      console.error('❌ Response:', err.response);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (event) => {
    const confirmed = await confirm({
      title: 'Deletar Evento',
      message: `Tem certeza que deseja deletar "${event.name}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Deletar',
      cancelText: 'Cancelar',
      type: 'danger',
      icon: '🗑️'
    });

    if (!confirmed) return;

    try {
      await specialEventsService.delete(event.id);
      notify.success('Evento deletado com sucesso!', 'Evento Removido');
      loadEvents();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Erro ao deletar evento', 'Erro');
    }
  };

  const handleClone = async (event) => {
    const confirmed = await confirm({
      title: 'Clonar Time de Ataque',
      message: `Clonar "${event.name}"? Será criado um novo evento copiando apenas o time de ataque (membros fixos das PTs 1 a 13). Buffs rotativos e suporte não serão copiados, e os grupos serão gerados automaticamente.`,
      confirmText: 'Clonar',
      cancelText: 'Cancelar',
      type: 'warning',
      icon: '📋'
    });

    if (!confirmed) return;

    try {
      const result = await specialEventsService.clone(event.id);
      notify.success(result.message || 'Evento clonado com sucesso!', 'Evento Clonado');
      navigate(`/app/admin/special-events/${result.event.id}`);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Erro ao clonar evento', 'Erro');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: 'bg-gray-500/20 text-gray-300',
      ACTIVE: 'bg-green-500/20 text-green-300',
      COMPLETED: 'bg-blue-500/20 text-blue-300',
      CANCELLED: 'bg-red-500/20 text-red-300'
    };

    const labels = {
      DRAFT: 'Rascunho',
      ACTIVE: 'Ativo',
      COMPLETED: 'Concluído',
      CANCELLED: 'Cancelado'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badges[status] || ''}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const badges = {
      PVP: 'bg-red-500/20 text-red-300',
      BC: 'bg-purple-500/20 text-purple-300',
      RAID: 'bg-orange-500/20 text-orange-300',
      DUNGEON: 'bg-cyan-500/20 text-cyan-300',
      CUSTOM: 'bg-gray-500/20 text-gray-300'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badges[type] || ''}`}>
        {type}
      </span>
    );
  };

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-1">
                🎮 Eventos Especiais
              </h1>
              <p className="text-gray-300">
                Gerencie eventos com grupos personalizados e drag-and-drop
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => navigate('/app/admin/special-events/dream-team')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shadow-lg shadow-purple-500/20"
              >
                <Sparkles className="w-4 h-4" />
                Dream Team
              </Button>
              <Button
                onClick={() => navigate('/app/admin/special-events/new')}
                className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 shadow-lg shadow-orange-500/20"
              >
                <Plus className="w-4 h-4" />
                Novo Evento
              </Button>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center gap-2 flex-1">
              <Filter className="w-4 h-4 text-gray-400" />
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="flex-1"
              >
                <option value="">Todos os Status</option>
                <option value="DRAFT">Rascunho</option>
                <option value="ACTIVE">Ativo</option>
                <option value="COMPLETED">Concluído</option>
                <option value="CANCELLED">Cancelado</option>
              </Select>
            </div>

            <Select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="flex-1"
            >
              <option value="">Todos os Tipos</option>
              <option value="PVP">PVP</option>
              <option value="BC">BC</option>
              <option value="RAID">Raid</option>
              <option value="DUNGEON">Dungeon</option>
              <option value="CUSTOM">Personalizado</option>
            </Select>
          </div>
        </div>

        {/* Mensagem de erro */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-2xl p-4 backdrop-blur-xl">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Lista de eventos */}
        {events.length === 0 ? (
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-12 border border-white/10 shadow-xl text-center">
            <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-gray-300 text-lg mb-4">Nenhum evento especial criado ainda</p>
            <Button
              onClick={() => navigate('/app/admin/special-events/new')}
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 shadow-lg shadow-orange-500/20"
            >
              <Plus className="w-4 h-4" />
              Criar Primeiro Evento
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((event) => (
              <div key={event.id} className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-orange-500/50 shadow-xl transition-all duration-300 hover:shadow-orange-500/20">
              <div className="space-y-4">
                {/* Header do card */}
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{event.name}</h3>
                    <div className="flex gap-2 mb-3">
                      {getStatusBadge(event.status)}
                      {getTypeBadge(event.eventType)}
                    </div>
                  </div>
                </div>

                {/* Descrição */}
                {event.description && (
                  <p className="text-slate-400 text-sm line-clamp-2">
                    {event.description}
                  </p>
                )}

                {/* Informações */}
                <div className="space-y-2 text-sm">
                  {event.eventDate && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(event.eventDate)}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-slate-400">
                    <Users className="w-4 h-4" />
                    <span>{event.participantsCount || 0} participantes</span>
                  </div>

                  <div className="flex items-center gap-2 text-slate-400">
                    <UsersRound className="w-4 h-4" />
                    <span>{event.partiesCount || 0} grupos</span>
                  </div>
                </div>

                {/* Configurações */}
                <div className="flex gap-2 text-xs text-slate-500">
                  <span>Max: {event.maxPartySize} por grupo</span>
                  {event.requireBuff && <span>• Requer buff</span>}
                </div>

                {/* Ações */}
                <div className="flex gap-2 pt-4 border-t border-slate-700">
                  <Button
                    onClick={() => navigate(`/app/admin/special-events/${event.id}`)}
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                  >
                    <Eye className="w-4 h-4" />
                    Gerenciar
                  </Button>
                  <Button
                    onClick={() => navigate(`/app/admin/special-events/${event.id}/edit`)}
                    variant="secondary"
                    size="sm"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleClone(event)}
                    variant="secondary"
                    size="sm"
                    title="Clonar time de ataque"
                    className="hover:bg-orange-500/20 hover:text-orange-400"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(event)}
                    variant="secondary"
                    size="sm"
                    className="hover:bg-red-500/20 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de confirmação */}
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
        icon={confirmState.icon}
      />
    </div>
  );
};

export default SpecialEventsList;
